import { useCallback, useEffect, useMemo, useState } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  CalendarDays,
  MapPin,
  AlertTriangle,
  Dumbbell,
  Swords,
  Trophy,
  Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  match: { label: "Partita", color: "bg-red-100 text-red-700 border-red-200", Icon: Swords },
  training: { label: "Allenamento", color: "bg-sky-100 text-sky-700 border-sky-200", Icon: Dumbbell },
  tournament: { label: "Torneo", color: "bg-amber-100 text-amber-700 border-amber-200", Icon: Trophy },
  meeting: { label: "Riunione", color: "bg-violet-100 text-violet-700 border-violet-200", Icon: UsersIcon },
  social: { label: "Evento", color: "bg-emerald-100 text-emerald-700 border-emerald-200", Icon: CalendarDays },
  other: { label: "Altro", color: "bg-slate-100 text-slate-700 border-slate-200", Icon: CalendarDays },
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function isoPlusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  } catch {
    return iso;
  }
}

function endTime(time, duration) {
  if (!time || !duration) return "";
  const [h, m] = time.split(":").map(Number);
  const total = h * 60 + m + duration;
  const eh = Math.floor(total / 60) % 24;
  const em = total % 60;
  return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
}

export default function FieldCalendarPage() {
  const { activeTeam } = useTeams();
  const [fields, setFields] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState("");
  const [bookings, setBookings] = useState([]);
  const [fromDate, setFromDate] = useState(todayISO());
  const [toDate, setToDate] = useState(isoPlusDays(60));
  const [loading, setLoading] = useState(false);

  const loadFields = useCallback(async () => {
    if (!activeTeam) return;
    try {
      const { data } = await api.get(`/teams/${activeTeam.id}/fields`);
      setFields(data || []);
      if (data?.length && !selectedFieldId) {
        setSelectedFieldId(data[0].id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento campi");
    }
  }, [activeTeam, selectedFieldId]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const loadBookings = useCallback(async () => {
    if (!selectedFieldId) {
      setBookings([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/fields/${selectedFieldId}/bookings`, {
        params: { from: fromDate, to: toDate },
      });
      setBookings(data?.bookings || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento prenotazioni");
    } finally {
      setLoading(false);
    }
  }, [selectedFieldId, fromDate, toDate]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const byDate = useMemo(() => {
    const m = {};
    for (const b of bookings) {
      (m[b.date] = m[b.date] || []).push(b);
    }
    // sort each day chronologically
    Object.values(m).forEach((arr) =>
      arr.sort((a, b) => (a.time || "").localeCompare(b.time || ""))
    );
    return m;
  }, [bookings]);

  const conflictCount = useMemo(
    () => bookings.filter((b) => b.conflict).length,
    [bookings]
  );

  const teamColorIndex = useMemo(() => {
    const map = {};
    let i = 0;
    const palette = [
      "#0ea5e9",
      "#10b981",
      "#f59e0b",
      "#ef4444",
      "#8b5cf6",
      "#ec4899",
      "#14b8a6",
      "#f97316",
    ];
    for (const b of bookings) {
      if (!map[b.team_id]) {
        map[b.team_id] = b.team_color || palette[i % palette.length];
        i += 1;
      }
    }
    return map;
  }, [bookings]);

  const selectedField = fields.find((f) => f.id === selectedFieldId);

  if (!activeTeam) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-slate-400">
        Seleziona una squadra
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="field-calendar-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow">Pianificazione</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <CalendarDays size={24} className="text-sky-600" />
            Calendario campi multi-squadra
          </h1>
          <p className="text-slate-500 mt-1">
            Visualizza tutte le prenotazioni delle tue squadre sullo stesso
            campo per evitare sovrapposizioni.
          </p>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-5">
            <div className="label-eyebrow mb-1">Campo</div>
            <Select value={selectedFieldId} onValueChange={setSelectedFieldId}>
              <SelectTrigger data-testid="field-calendar-select">
                <SelectValue placeholder="Seleziona un campo…" />
              </SelectTrigger>
              <SelectContent>
                {fields.length === 0 && (
                  <SelectItem value="__empty__" disabled>
                    Nessun campo registrato
                  </SelectItem>
                )}
                {fields.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3">
            <div className="label-eyebrow mb-1">Dal</div>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="field-calendar-from"
            />
          </div>
          <div className="md:col-span-3">
            <div className="label-eyebrow mb-1">Al</div>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="field-calendar-to"
            />
          </div>
          <div className="md:col-span-1">
            <Badge
              variant="outline"
              className="w-full justify-center h-10 text-sm"
              data-testid="field-calendar-count"
            >
              {bookings.length}
            </Badge>
          </div>
        </div>
        {selectedField && (
          <div className="text-xs text-slate-500 mt-3 flex items-center gap-1">
            <MapPin size={11} />
            {selectedField.name}
            {selectedField.address && ` · ${selectedField.address}`}
          </div>
        )}
      </Card>

      {conflictCount > 0 && (
        <Card
          className="p-3 border-red-200 bg-red-50 flex items-center gap-2"
          data-testid="field-calendar-conflicts"
        >
          <AlertTriangle size={18} className="text-red-600 shrink-0" />
          <div className="text-sm text-red-800">
            <strong>{conflictCount}</strong> prenotazioni si sovrappongono.
            Sposta o annulla le entry con il bordo rosso qui sotto.
          </div>
        </Card>
      )}

      {/* Day-by-day timeline */}
      <div className="space-y-4">
        {loading && bookings.length === 0 && (
          <Card className="p-10 text-center text-slate-400">Caricamento…</Card>
        )}
        {!loading && bookings.length === 0 && (
          <Card className="p-10 text-center text-slate-400 border-dashed">
            Nessuna prenotazione per questo campo nel periodo selezionato.
          </Card>
        )}
        {Object.keys(byDate)
          .sort()
          .map((date) => {
            const dayItems = byDate[date];
            const hasConflict = dayItems.some((b) => b.conflict);
            return (
              <div key={date} data-testid={`day-${date}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="font-heading font-bold text-lg">
                    {formatDate(date)}
                  </div>
                  <span className="text-xs text-slate-400 uppercase tracking-wider">
                    {dayItems.length} {dayItems.length === 1 ? "evento" : "eventi"}
                  </span>
                  {hasConflict && (
                    <Badge
                      variant="outline"
                      className="bg-red-50 text-red-700 border-red-200 text-[10px]"
                    >
                      <AlertTriangle size={10} className="mr-1" />
                      Conflitto
                    </Badge>
                  )}
                </div>
                <div className="space-y-2">
                  {dayItems.map((b) => {
                    const meta = KIND_META[b.kind] || KIND_META.other;
                    const Icon = meta.Icon;
                    const color = teamColorIndex[b.team_id] || "#94a3b8";
                    return (
                      <Card
                        key={`${b.kind}-${b.id}`}
                        className={`p-3 border ${
                          b.conflict
                            ? "border-red-300 ring-2 ring-red-100"
                            : "border-slate-200"
                        }`}
                        data-testid={`booking-${b.id}`}
                      >
                        <div className="flex items-center gap-3 flex-wrap">
                          <div
                            className="h-10 w-1.5 rounded-full shrink-0"
                            style={{ background: color }}
                          />
                          <div className="font-mono text-sm font-bold text-slate-700 min-w-[110px]">
                            {b.time || "—"}
                            {b.time && b.duration_min ? (
                              <span className="text-slate-400 font-normal">
                                {" "}→ {endTime(b.time, b.duration_min)}
                              </span>
                            ) : null}
                          </div>
                          <Badge
                            variant="outline"
                            className={`${meta.color} font-medium`}
                          >
                            <Icon size={11} className="mr-1" />
                            {meta.label}
                          </Badge>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{b.title}</div>
                            <div className="text-xs text-slate-500 truncate">
                              {b.team_name}
                              {b.team_category && ` · ${b.team_category}`}
                            </div>
                          </div>
                          {b.conflict && (
                            <Badge
                              variant="outline"
                              className="bg-red-50 text-red-700 border-red-200 text-[10px]"
                            >
                              <AlertTriangle size={10} className="mr-1" />
                              Conflitto
                            </Badge>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>

      <div className="text-xs text-slate-400 flex items-center gap-2">
        <CalendarDays size={12} />
        I campi sono condivisi tra tutte le tue squadre, quindi questa vista
        aggrega allenamenti, partite ed eventi di ogni roster sullo stesso
        impianto.
      </div>
    </div>
  );
}
